/**
 * src/admin-ui/src/components/edge-table/EdgeDataTable.tsx
 *
 * Generic full-screen data table of the project-wide "full-screen page"
 * pattern (see CLAUDE.md). One component covers every table page:
 *
 * - edge-to-edge table with a sticky header pinned to the top of the
 *   screen; the whole header label is an Excel-like filter trigger for
 *   columns that declare `filterKind` (data must be fully loaded on the
 *   client for filters to make sense);
 * - a fixed bottom bar flush against the navigation footer: leading
 *   "Всего: N" tile, page-specific tiles (`bottomTiles` — actions,
 *   search, date ranges…), and a trailing pagination tile;
 * - stacked cards instead of the table on mobile (<sm) widths.
 *
 * Pagination is either client-side (`clientPageSize` slices the
 * filtered rows) or server-side (`server` prop: the parent owns
 * page/pageSize state and passes exactly one page in `rows`; header
 * filters are not available in this mode).
 *
 * Minimal usage:
 *
 * ```tsx
 * <EdgeDataTable
 *   ariaLabel="Список API ключей"
 *   columns={[{ id: "id", label: "ID", numeric: true, filterKind: "number" }]}
 *   rows={keys}
 *   getCell={(row, id) => ({ text: String(row[id]), num: row[id] })}
 *   rowKey={(row) => row.id}
 *   emptyMessage="Пока пусто"
 *   renderMobileCard={(row) => <>{row.id}</>}
 *   clientPageSize={100}
 *   bottomTiles={<BarTile>…actions…</BarTile>}
 * />
 * ```
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CenteredState, DesktopOnly, MobileOnly } from "../../styles/common";
import {
  applyColumnFilters,
  ColumnFilterState,
  ColumnKind,
  emptyFilter,
  FilterableCell,
  uniqueColumnValues,
} from "../../utils/usageTableFilters";
import ColumnFilterPopover from "./ColumnFilterPopover";
import {
  BarTile,
  BottomBar,
  EdgeTable,
  FullBleedTableViewport,
  HeaderCell,
  HeaderStatic,
  MobileCard,
  MobileCards,
  TotalTile,
} from "./EdgeTableLayout.styles";

/** Column description of an {@link EdgeDataTable}. */
export interface EdgeColumn<Row> {
  /** Stable column id, also passed to `getCell`. */
  id: string;
  /** Header label; doubles as the filter trigger text. */
  label: string;
  /** Right-align the column (numbers). */
  numeric?: boolean;
  /**
   * Enable the Excel-like header filter of this kind. Leave undefined
   * for non-filterable columns (editable cells, action columns, or any
   * table whose rows are paginated on the server).
   */
  filterKind?: ColumnKind;
  /** Custom cell renderer; defaults to `getCell(row, id).text`. */
  render?: (row: Row) => React.ReactNode;
}

/** Server-side pagination contract (the parent owns the query state). */
export interface EdgeServerPagination {
  /** Total row count reported by the backend. */
  total: number;
  /** Zero-based page index. */
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** When set, a page-size select is rendered in the pagination tile. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
}

export interface EdgeDataTableProps<Row> {
  /** Accessible name of the table and of the mobile card list. */
  ariaLabel: string;
  columns: EdgeColumn<Row>[];
  /** All rows (client mode) or the current server page (server mode). */
  rows: Row[];
  /**
   * Cell accessor used for filtering and default rendering. `text` is
   * the display value; `num` powers numeric filters.
   */
  getCell: (row: Row, columnId: string) => FilterableCell;
  rowKey: (row: Row, index: number) => React.Key;
  /** Shows a centered spinner while true and no rows are loaded yet. */
  loading?: boolean;
  emptyMessage: string;
  /** Row click handler — applied to table rows and to mobile cards. */
  onRowClick?: (row: Row) => void;
  /**
   * Card CONTENT for mobile (<sm) widths. The content is wrapped in the
   * shared `MobileCard` (padding, grid rhythm, divider) automatically —
   * return bare elements, not a padded container.
   */
  renderMobileCard: (row: Row) => React.ReactNode;
  /** Client-side page size; omit together with `server` to disable pagination. */
  clientPageSize?: number;
  /** Server-side pagination; mutually exclusive with `clientPageSize`. */
  server?: EdgeServerPagination;
  /** Extra bottom-bar tiles (wrap each control group in `BarTile`). */
  bottomTiles?: React.ReactNode;
}

const formatNumber = (value: number): string => value.toLocaleString("ru-RU");

/**
 * Full-screen table with header filters, bottom control bar and mobile
 * cards. See the module docstring for the composition contract.
 */
function EdgeDataTable<Row>({
  ariaLabel,
  columns,
  rows,
  getCell,
  rowKey,
  loading = false,
  emptyMessage,
  onRowClick,
  renderMobileCard,
  clientPageSize,
  server,
  bottomTiles,
}: EdgeDataTableProps<Row>): React.ReactElement {
  const [filters, setFilters] = useState<Record<string, ColumnFilterState>>({});
  const [page, setPage] = useState(0);

  // Drop filters of columns that disappeared (e.g. group-by change).
  const columnIds = columns.map((column) => column.id).join("\u0000");
  useEffect(() => {
    const ids = new Set(columnIds.split("\u0000"));
    setFilters((current) => {
      const stale = Object.keys(current).filter((id) => !ids.has(id));
      if (stale.length === 0) return current;
      const next = { ...current };
      stale.forEach((id) => delete next[id]);
      return next;
    });
  }, [columnIds]);

  const columnKinds = useMemo(
    () =>
      Object.fromEntries(
        columns
          .filter((column) => column.filterKind)
          .map((column) => [column.id, column.filterKind as ColumnKind]),
      ),
    [columns],
  );

  const uniqueValues = useMemo(
    () =>
      Object.fromEntries(
        columns
          .filter((column) => column.filterKind)
          .map((column) => [
            column.id,
            uniqueColumnValues(rows, getCell, column.id),
          ]),
      ),
    [columns, rows, getCell],
  );

  const filteredRows = useMemo(
    () =>
      server ? rows : applyColumnFilters(rows, filters, getCell, columnKinds),
    [server, rows, filters, getCell, columnKinds],
  );

  // Client mode slices the filtered rows; server mode shows rows as-is.
  const pageSize = server?.pageSize ?? clientPageSize ?? filteredRows.length;
  const pageCount = Math.max(
    1,
    Math.ceil(filteredRows.length / (pageSize || 1)),
  );
  const safePage = server ? server.page : Math.min(page, pageCount - 1);
  const pageRows = server
    ? filteredRows
    : filteredRows.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const total = server ? server.total : filteredRows.length;
  const filteredNote =
    !server && filteredRows.length !== rows.length
      ? ` (отфильтровано из ${formatNumber(rows.length)})`
      : "";
  const rangeFrom = total === 0 ? 0 : safePage * pageSize + 1;
  const rangeTo = server
    ? Math.min((safePage + 1) * pageSize, server.total)
    : Math.min((safePage + 1) * pageSize, filteredRows.length);
  const lastPage = server
    ? Math.max(0, Math.ceil(server.total / server.pageSize) - 1)
    : pageCount - 1;

  const changePage = (nextPage: number): void => {
    if (server) server.onPageChange(nextPage);
    else setPage(nextPage);
  };

  const setColumnFilter = (
    columnId: string,
    filter: ColumnFilterState,
  ): void => {
    setFilters((current) => ({ ...current, [columnId]: filter }));
    setPage(0);
  };

  const showPagination = Boolean(server || clientPageSize);

  return (
    <>
      {loading && rows.length === 0 ? (
        <CenteredState>
          <CircularProgress />
        </CenteredState>
      ) : (
        <>
          <DesktopOnly>
            <FullBleedTableViewport>
              <EdgeTable stickyHeader aria-label={ariaLabel}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <HeaderCell key={column.id}>
                        {column.filterKind ? (
                          <ColumnFilterPopover
                            label={column.label}
                            kind={column.filterKind}
                            numeric={column.numeric}
                            values={uniqueValues[column.id] ?? []}
                            filter={filters[column.id] ?? emptyFilter()}
                            onChange={(filter) =>
                              setColumnFilter(column.id, filter)
                            }
                          />
                        ) : (
                          <HeaderStatic numeric={column.numeric}>
                            {column.label}
                          </HeaderStatic>
                        )}
                      </HeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((row, index) => (
                    <TableRow
                      key={rowKey(row, index)}
                      hover
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={onRowClick ? { cursor: "pointer" } : undefined}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.numeric ? "right" : "left"}
                        >
                          {column.render
                            ? column.render(row)
                            : getCell(row, column.id).text}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </EdgeTable>
              {pageRows.length === 0 && (
                <CenteredState>
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </CenteredState>
              )}
            </FullBleedTableViewport>
          </DesktopOnly>

          <MobileOnly aria-label={ariaLabel}>
            <MobileCards>
              {pageRows.map((row, index) => (
                <MobileCard
                  key={rowKey(row, index)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {renderMobileCard(row)}
                </MobileCard>
              ))}
              {pageRows.length === 0 && (
                <MobileCard>
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </MobileCard>
              )}
            </MobileCards>
          </MobileOnly>
        </>
      )}

      <BottomBar>
        <TotalTile>
          Всего: {formatNumber(total)}
          {filteredNote}
        </TotalTile>

        {bottomTiles}

        {showPagination && (
          <BarTile>
            {server?.pageSizeOptions && (
              <>
                <Typography variant="body2" color="text.secondary">
                  На странице
                </Typography>
                <Select
                  size="small"
                  variant="standard"
                  disableUnderline
                  value={server.pageSize}
                  inputProps={{ "aria-label": "Строк на странице" }}
                  onChange={(event) =>
                    server.onPageSizeChange?.(Number(event.target.value))
                  }
                >
                  {server.pageSizeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}
            <Typography variant="body2" color="text.secondary">
              {rangeFrom}–{rangeTo} из {formatNumber(total)}
            </Typography>
            <IconButton
              size="small"
              aria-label="Предыдущая страница"
              sx={{ minHeight: 32 }}
              disabled={safePage === 0}
              onClick={() => changePage(safePage - 1)}
            >
              <ChevronLeftIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Следующая страница"
              sx={{ minHeight: 32 }}
              disabled={safePage >= lastPage}
              onClick={() => changePage(safePage + 1)}
            >
              <ChevronRightIcon fontSize="inherit" />
            </IconButton>
          </BarTile>
        )}
      </BottomBar>
    </>
  );
}

export default EdgeDataTable;
